import {
  CHAT_CONVERSATIONS_PAGE_SIZE,
  CHAT_MESSAGES_PAGE_SIZE
} from './constants';

export const parseConversations = (payload) => {
  const rows = Array.isArray(payload?.conversations) ? payload.conversations : [];
  // Normalize so inbox filters (=== DIRECT) are not tripped by string enums.
  return rows.map((row) =>
    row && row.conversation_type != null
      ? { ...row, conversation_type: Number(row.conversation_type) }
      : row
  );
};

export const parseMessages = (payload) =>
  Array.isArray(payload?.messages) ? payload.messages : [];

export const parseConversation = (payload) =>
  payload?.conversation || null;

export const toInboxRow = (conversation) => {
  if (!conversation) return null;
  const member = Array.isArray(conversation.members) ? conversation.members[0] : null;
  const residentName = member
    ? `${member.first_name || ''} ${member.last_name || ''}`.trim()
    : conversation.resident_name || '';
  return {
    ...conversation,
    resident_name: conversation.resident_name || residentName,
    unit_num: conversation.unit_num || member?.unit_num || '',
    resident_avatar: conversation.resident_avatar || member?.avatar || '',
    unread_count: conversation.unread_count || 0
  };
};

export const toChronological = (messages) => [...messages].reverse();

export const sameConversationId = (a, b) => Number(a) === Number(b);

export const hasAnyUnread = (conversations) =>
  (conversations || []).some((c) => Number(c.unread_count) > 0);

const keepDisplayField = (incoming, fallback = '') => {
  if (incoming == null) return fallback || '';
  if (typeof incoming === 'string') {
    const trimmed = incoming.trim();
    return trimmed || fallback || '';
  }
  return incoming || fallback || '';
};

export const mergeConversationDisplay = (incoming, existing) => {
  if (!incoming) return existing || null;
  if (!existing) return incoming;
  return {
    ...existing,
    ...incoming,
    resident_name: keepDisplayField(incoming.resident_name, existing.resident_name),
    unit_num: keepDisplayField(incoming.unit_num, existing.unit_num),
    resident_avatar: keepDisplayField(incoming.resident_avatar, existing.resident_avatar),
    title: keepDisplayField(incoming.title, existing.title)
  };
};

const messageKeyMatch = (message, candidate) => {
  if (!message || !candidate) return false;
  if (message.id && candidate.id && Number(message.id) === Number(candidate.id)) return true;
  if (message.client_msg_id && candidate.client_msg_id) {
    return message.client_msg_id === candidate.client_msg_id;
  }
  return false;
};

export const messageAlreadyExists = (messages, message) =>
  (messages || []).some((m) => messageKeyMatch(message, m));

export const appendUniqueMessage = (messages, message) => {
  if (!message?.id && !message?.client_msg_id) return messages || [];
  if (messageAlreadyExists(messages, message)) return messages || [];
  return [...(messages || []), message];
};

export const mergeFetchedMessages = (fetched, existing) => {
  if (!existing?.length) return fetched;
  if (!fetched?.length) return existing;

  const fetchedIds = new Set(fetched.map((m) => Number(m.id)).filter(Boolean));
  const fetchedClientIds = new Set(fetched.map((m) => m.client_msg_id).filter(Boolean));
  const extras = existing.filter((m) => {
    if (m?.id && fetchedIds.has(Number(m.id))) return false;
    if (m?.client_msg_id && fetchedClientIds.has(m.client_msg_id)) return false;
    return Boolean(m?.id || m?.client_msg_id);
  });
  if (!extras.length) return fetched;
  return [...fetched, ...extras].sort((a, b) => Number(a.id) - Number(b.id));
};

const preferNewerPreview = (merged, previous, incoming) => {
  const prevAt = previous?.last_message_at ? Date.parse(previous.last_message_at) : 0;
  const nextAt = incoming?.last_message_at ? Date.parse(incoming.last_message_at) : 0;
  if (Number.isFinite(prevAt) && Number.isFinite(nextAt) && prevAt > nextAt) {
    return {
      ...merged,
      last_message_at: previous.last_message_at,
      last_message_preview: previous.last_message_preview,
      unread_count: previous.unread_count
    };
  }
  return merged;
};

export const mergeInboxRow = (incoming, previous) => {
  if (!previous) return incoming;
  return preferNewerPreview(mergeConversationDisplay(incoming, previous), previous, incoming);
};

export const LIST_MERGE = {
  REPLACE: 'replace',
  APPEND: 'append',
  SOFT: 'soft'
};

export const resolveListMergeMode = ({ page, replace }) => {
  const safePage = Number(page) || 1;
  if (replace !== false && safePage <= 1) return LIST_MERGE.REPLACE;
  if (safePage > 1) return LIST_MERGE.APPEND;
  return LIST_MERGE.SOFT;
};

export const mergeConversationList = (existing, incoming, mode) => {
  const current = existing || [];
  const next = incoming || [];
  const prevById = new Map(current.map((c) => [Number(c.id), c]));
  const mergeRow = (row) => mergeInboxRow(row, prevById.get(Number(row.id)));

  if (mode === LIST_MERGE.REPLACE) {
    return next.map(mergeRow);
  }

  if (mode === LIST_MERGE.APPEND) {
    const existingIds = new Set(current.map((c) => Number(c.id)));
    const appended = next.filter((c) => !existingIds.has(Number(c.id))).map(mergeRow);
    return [...current, ...appended];
  }

  const incomingIds = new Set(next.map((c) => Number(c.id)));
  const refreshed = next.map(mergeRow);
  const rest = current.filter((c) => !incomingIds.has(Number(c.id)));
  return [...refreshed, ...rest];
};

export const listHasMoreFromPage = (incoming) =>
  (incoming || []).length >= CHAT_CONVERSATIONS_PAGE_SIZE;

export const messagesHasMoreFromPage = (fetched) =>
  (fetched || []).length >= CHAT_MESSAGES_PAGE_SIZE;

export const applyRealtimeToInbox = ({
  conversations,
  selectedConversation,
  messages,
  message,
  conversation,
  currentUserId
}) => {
  const row = toInboxRow(conversation);
  if (!row?.id || !message?.id) {
    return { conversations, selectedConversation, messages, changed: false };
  }

  const isSelected = sameConversationId(selectedConversation?.id, conversation.id);
  const isOwn = currentUserId > 0 && Number(message.sender_user_id) === currentUserId;
  const existing = (conversations || []).find((c) => sameConversationId(c.id, row.id));
  const displaySource = existing || (isSelected ? selectedConversation : null);
  const prevUnread = Number(existing?.unread_count) || Number(displaySource?.unread_count) || 0;
  const nextUnread = isOwn || isSelected ? (isSelected ? 0 : prevUnread) : prevUnread + 1;

  const nextRow = mergeConversationDisplay(
    {
      ...(displaySource || {}),
      ...row,
      last_message_at: message.created_at || row.last_message_at || displaySource?.last_message_at,
      last_message_preview:
        message.body || row.last_message_preview || displaySource?.last_message_preview || '',
      unread_count: nextUnread
    },
    displaySource
  );

  const without = (conversations || []).filter((c) => !sameConversationId(c.id, row.id));
  const nextConversations = [nextRow, ...without];
  const nextMessages = isSelected ? appendUniqueMessage(messages, message) : messages;
  const nextSelected = isSelected
    ? { ...mergeConversationDisplay(nextRow, selectedConversation), unread_count: 0 }
    : selectedConversation;

  return {
    conversations: nextConversations,
    selectedConversation: nextSelected,
    messages: nextMessages,
    changed: true
  };
};
